/**
 * Auth Error Page Component
 * @implements BS-001.3 - OAuth Provider Lock-In (user-facing error)
 *
 * Handles OAuth error redirects from the backend.
 * The backend redirects to /auth/error?error={code}&provider={provider}
 * when OAuth authentication fails. This page displays a clear error message
 * and provides navigation back to login.
 *
 * For provider_mismatch errors, tells the user which provider they originally
 * registered with so they can sign in with the correct one.
 */

import { useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Format a provider name for display (e.g., "github" -> "GitHub", "google" -> "Google")
 */
const formatProviderName = (provider: string): string => {
  const names: Record<string, string> = {
    github: 'GitHub',
    google: 'Google',
  };
  return names[provider.toLowerCase()] || provider;
};

/**
 * Build an error message from the error code and optional provider.
 * @implements BS-001.3 - OAuth Provider Lock-In
 */
const getErrorDetails = (
  code: string | null,
  provider: string | null,
): {
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
} => {
  if (!code) {
    return {
      title: 'Authentication Error',
      message:
        'An unknown error occurred during authentication. Please try again.',
      severity: 'error',
    };
  }

  switch (code) {
    case 'provider_mismatch': {
      const providerName = provider
        ? formatProviderName(provider)
        : 'a different provider';
      return {
        title: 'Wrong Sign-In Method',
        message: `This email is already registered with ${providerName}. Please sign in using ${providerName} instead.`,
        severity: 'warning',
      };
    }
    case 'provider_denied':
      return {
        title: 'Authorization Denied',
        message:
          'You denied authorization with the OAuth provider. Please try again if this was unintentional.',
        severity: 'info',
      };
    case 'email_exists':
      return {
        title: 'Email Already Registered',
        message:
          'This email is already registered with a different authentication method.',
        severity: 'warning',
      };
    case 'invalid_state':
      return {
        title: 'Session Expired',
        message:
          'Your authentication session has expired. Please try signing in again.',
        severity: 'error',
      };
    case 'code_expired':
      return {
        title: 'Authorization Expired',
        message:
          'The authorization code has expired. Please try signing in again.',
        severity: 'error',
      };
    default:
      return {
        title: 'Authentication Error',
        message: 'An error occurred during authentication. Please try again.',
        severity: 'error',
      };
  }
};

export const AuthErrorPage = () => {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  const provider = searchParams.get('provider');

  const errorDetails = useMemo(
    () => getErrorDetails(errorCode, provider),
    [errorCode, provider],
  );

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>

          <Typography variant="h4" component="h1" gutterBottom align="center">
            {errorDetails.title}
          </Typography>

          <Alert severity={errorDetails.severity} sx={{ mb: 3 }}>
            {errorDetails.message}
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              fullWidth
              size="large"
              sx={{ textTransform: 'none' }}
            >
              Back to Sign In
            </Button>
            <Button
              component={RouterLink}
              to="/register"
              variant="text"
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              Create an Account
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
